import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TimeEntry {
  id: string;
  tenantId: string;
  userId: string;
  userName: string;
  jobId: string;
  jobTitle: string;
  clientName: string;
  taskId: string | null;
  taskTitle: string | null;
  date: string;
  durationMinutes: number;
  description: string | null;
  isBillable: boolean;
  hourlyRate: number | null;
  approvalStatus: ApprovalStatus;
  approvedById: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface WeeklyTimesheetRow {
  jobId: string;
  jobTitle: string;
  clientName: string;
  entries: Record<string, TimesheetCell>; // keyed by ISO date string (YYYY-MM-DD)
}

export interface TimesheetCell {
  id: string | null;
  durationMinutes: number;
  approvalStatus: ApprovalStatus;
  rejectionComment: string | null;
}

export interface WeeklyTimesheet {
  weekStart: string;
  weekEnd: string;
  rows: WeeklyTimesheetRow[];
  dailyTotals: Record<string, number>; // keyed by ISO date string
  weeklyTotal: number;
  overallApprovalStatus: ApprovalStatus;
}

export interface TimeEntriesListParams {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  jobId?: string;
  userId?: string;
  approvalStatus?: ApprovalStatus | '';
  isBillable?: boolean;
}

export interface CreateTimeEntryRequest {
  jobId: string;
  taskId?: string;
  date: string;
  durationMinutes: number;
  description?: string;
  isBillable: boolean;
}

export interface UpdateTimeEntryRequest {
  id: string;
  jobId?: string;
  taskId?: string | null;
  date?: string;
  durationMinutes?: number;
  description?: string | null;
  isBillable?: boolean;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Timer State (Zustand) ───────────────────────────────────────────────────

export interface TimerState {
  isRunning: boolean;
  isPaused: boolean;
  startedAt: number | null;
  pausedAt: number | null;
  accumulatedMs: number;
  jobId: string | null;
  taskId: string | null;
  isBillable: boolean;
  description: string;

  // Actions
  start: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => {
    elapsedMs: number;
    jobId: string | null;
    taskId: string | null;
    isBillable: boolean;
    description: string;
  };
  reset: () => void;
  setJob: (jobId: string | null) => void;
  setTask: (taskId: string | null) => void;
  setBillable: (isBillable: boolean) => void;
  setDescription: (description: string) => void;
  getElapsedMs: () => number;
}

export const useTimerStore = create<TimerState>((set, get) => ({
  isRunning: false,
  isPaused: false,
  startedAt: null,
  pausedAt: null,
  accumulatedMs: 0,
  jobId: null,
  taskId: null,
  isBillable: true,
  description: '',

  start: () => {
    set({
      isRunning: true,
      isPaused: false,
      startedAt: Date.now(),
      pausedAt: null,
      accumulatedMs: 0,
    });
  },

  pause: () => {
    const state = get();
    if (!state.isRunning || state.isPaused) return;

    const now = Date.now();
    const elapsed = state.startedAt ? now - state.startedAt : 0;

    set({
      isPaused: true,
      pausedAt: now,
      accumulatedMs: state.accumulatedMs + elapsed,
      startedAt: null,
    });
  },

  resume: () => {
    const state = get();
    if (!state.isPaused) return;

    set({
      isPaused: false,
      pausedAt: null,
      startedAt: Date.now(),
    });
  },

  stop: () => {
    const state = get();
    const elapsedMs = state.getElapsedMs();

    const result = {
      elapsedMs,
      jobId: state.jobId,
      taskId: state.taskId,
      isBillable: state.isBillable,
      description: state.description,
    };

    set({
      isRunning: false,
      isPaused: false,
      startedAt: null,
      pausedAt: null,
      accumulatedMs: 0,
    });

    return result;
  },

  reset: () => {
    set({
      isRunning: false,
      isPaused: false,
      startedAt: null,
      pausedAt: null,
      accumulatedMs: 0,
      jobId: null,
      taskId: null,
      isBillable: true,
      description: '',
    });
  },

  setJob: (jobId) => set({ jobId }),
  setTask: (taskId) => set({ taskId }),
  setBillable: (isBillable) => set({ isBillable }),
  setDescription: (description) => set({ description }),

  getElapsedMs: () => {
    const state = get();
    if (!state.isRunning && !state.isPaused) return 0;

    if (state.isPaused) {
      return state.accumulatedMs;
    }

    const now = Date.now();
    const currentSegment = state.startedAt ? now - state.startedAt : 0;
    return state.accumulatedMs + currentSegment;
  },
}));

// ─── Query Keys ──────────────────────────────────────────────────────────────

const timeEntryKeys = {
  all: ['time-entries'] as const,
  lists: () => [...timeEntryKeys.all, 'list'] as const,
  list: (params: TimeEntriesListParams) => [...timeEntryKeys.lists(), params] as const,
  details: () => [...timeEntryKeys.all, 'detail'] as const,
  detail: (id: string) => [...timeEntryKeys.details(), id] as const,
  weekly: (weekStart: string, userId?: string) =>
    [...timeEntryKeys.all, 'weekly', weekStart, userId] as const,
};

// ─── Query Hooks ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch paginated time entries with filtering.
 * Supports date range, job, user, and approval status filters.
 */
export function useTimeEntries(params: TimeEntriesListParams = {}) {
  return useQuery({
    queryKey: timeEntryKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.startDate) queryParams.set('startDate', params.startDate);
      if (params.endDate) queryParams.set('endDate', params.endDate);
      if (params.jobId) queryParams.set('jobId', params.jobId);
      if (params.userId) queryParams.set('userId', params.userId);
      if (params.approvalStatus) queryParams.set('approvalStatus', params.approvalStatus);
      if (params.isBillable !== undefined) queryParams.set('isBillable', String(params.isBillable));

      const response = await apiClient.get<ApiResponse<PaginatedResponse<TimeEntry>>>(
        `/time-entries?${queryParams.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to fetch weekly timesheet data grouped by day and job.
 * Returns rows (jobs) with cells for each day of the week.
 */
export function useWeeklyTimesheet(weekStart: string, userId?: string) {
  return useQuery({
    queryKey: timeEntryKeys.weekly(weekStart, userId),
    queryFn: async (): Promise<WeeklyTimesheet> => {
      const queryParams = new URLSearchParams({ weekStart });
      if (userId) queryParams.set('userId', userId);

      const response = await apiClient.get<
        ApiResponse<{
          user_id: string;
          week_start: string;
          week_end: string;
          days: Array<{
            date: string;
            entries: Array<{
              job_id: string;
              job_title: string;
              entries: Array<{
                id: string;
                duration_minutes: number;
                approval_status: string;
                rejection_comment: string | null;
              }>;
              totalMinutes: number;
            }>;
            totalMinutes: number;
          }>;
          totalMinutes: number;
          totalBillableMinutes: number;
          approval_summary: { pending: number; approved: number; rejected: number };
        }>
      >(`/time-entries/weekly?${queryParams.toString()}`);

      const apiData = response.data.data;

      // Transform API response to frontend WeeklyTimesheet shape
      const rowMap = new Map<string, WeeklyTimesheetRow>();
      const dailyTotals: Record<string, number> = {};

      for (const day of apiData.days) {
        dailyTotals[day.date] = day.totalMinutes;

        for (const jobGroup of day.entries) {
          if (!rowMap.has(jobGroup.job_id)) {
            rowMap.set(jobGroup.job_id, {
              jobId: jobGroup.job_id,
              jobTitle: jobGroup.job_title,
              clientName: '',
              entries: {},
            });
          }

          const row = rowMap.get(jobGroup.job_id)!;
          const firstEntry = jobGroup.entries[0];
          row.entries[day.date] = {
            id: firstEntry?.id || null,
            durationMinutes: jobGroup.totalMinutes,
            approvalStatus: (firstEntry?.approval_status as ApprovalStatus) || 'pending',
            rejectionComment: firstEntry?.rejection_comment || null,
          };
        }
      }

      // Determine overall approval status
      const { pending, rejected } = apiData.approval_summary;
      let overallApprovalStatus: ApprovalStatus = 'approved';
      if (rejected > 0) overallApprovalStatus = 'rejected';
      else if (pending > 0) overallApprovalStatus = 'pending';

      return {
        weekStart: apiData.week_start,
        weekEnd: apiData.week_end,
        rows: Array.from(rowMap.values()),
        dailyTotals,
        weeklyTotal: apiData.totalMinutes,
        overallApprovalStatus,
      };
    },
    enabled: !!weekStart,
  });
}

// ─── Mutation Hooks ──────────────────────────────────────────────────────────

/**
 * Hook to create a new time entry.
 */
export function useCreateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTimeEntryRequest) => {
      const response = await apiClient.post<ApiResponse<TimeEntry>>('/time-entries', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

/**
 * Hook to update an existing time entry.
 */
export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateTimeEntryRequest) => {
      const response = await apiClient.put<ApiResponse<TimeEntry>>(`/time-entries/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

/**
 * Hook to approve a time entry (manager/admin action).
 */
export function useApproveTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const response = await apiClient.post<ApiResponse<TimeEntry>>(
        `/time-entries/${entryId}/approve`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

/**
 * Hook to reject a time entry with a reason (manager/admin action).
 */
export function useRejectTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entryId, reason }: { entryId: string; reason: string }) => {
      const response = await apiClient.post<ApiResponse<TimeEntry>>(
        `/time-entries/${entryId}/reject`,
        { reason },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

/**
 * Hook to submit a week's time entries for approval.
 */
export function useSubmitWeekForApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weekStart: string) => {
      const response = await apiClient.post<ApiResponse<{ submitted: number }>>(
        '/time-entries/submit-week',
        { weekStart },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

/**
 * Hook to delete a time entry.
 */
export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      await apiClient.delete(`/time-entries/${entryId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

// ─── Rejection History ───────────────────────────────────────────────────────

export interface RejectionHistoryEntry {
  id: string;
  rejectionComment: string;
  rejectedAt: string;
  rejectedByName: string;
  entrySnapshot: {
    duration_minutes: number;
    entry_date: string;
    job_id: string;
    description: string | null;
    is_billable: boolean;
  } | null;
}

/**
 * Hook to fetch rejection history for a specific time entry.
 */
export function useRejectionHistory(timeEntryId: string | null) {
  return useQuery({
    queryKey: ['time-entries', 'rejection-history', timeEntryId],
    queryFn: async (): Promise<RejectionHistoryEntry[]> => {
      const response = await apiClient.get<
        ApiResponse<
          Array<{
            id: string;
            rejection_comment: string;
            rejected_at: string;
            rejected_by_name: string;
            entry_snapshot: Record<string, unknown> | null;
          }>
        >
      >(`/time-entries/${timeEntryId}/rejection-history`);

      return (response.data.data ?? []).map((r) => ({
        id: r.id,
        rejectionComment: r.rejection_comment,
        rejectedAt: r.rejected_at,
        rejectedByName: r.rejected_by_name || 'Unknown',
        entrySnapshot: r.entry_snapshot as RejectionHistoryEntry['entrySnapshot'],
      }));
    },
    enabled: !!timeEntryId,
  });
}
